/**
 * TKP-91 / tasarım s.22 md.1–6 — hukuki metin sihirbazının saf kuralları. Ekran değiştirmeden adım adım:
 * tip → şablon → filtreler, seçim yapıldıkça canlı önizleme; son adımda metin tek seferde üretilip yayımlanır.
 */

/** Sihirbazın adımları (sırayla). */
export const STEPS = Object.freeze(["type", "template", "filters"]);

/** "Rapor" ayrı bir tip: şablon listesi yerine Raporlar ekranına götürür; yalnız hukuk yöneticisine görünür (karar). */
export const REPORT_TYPE = "REPORT";

/**
 * Sihirbazın filtre adımında sorulan kriterler (md.3; TKP-197: 16 filtre, kullanıcının sırasıyla). Tümü çoklu seçim ve isteğe
 * bağlı; ilk beşi sabit görünür (`PINNED_CRITERIA`), diğerleri açılır panelde. Seçenekler envanterden (`filter-options` ucu).
 */
export const WIZARD_CRITERIA = Object.freeze([
  "department",
  "process",
  "datasubjectCategory",
  "datasubject",
  "dataCategory",
  "data",
  "processingPurpose",
  "processingLegalReason",
  "receiverGroup",
  "thirdParty",
  "abroad",
  "country",
  "directCollectMethod",
  "otherCollectMethod",
  "electronicStorage",
  "physicalStorage",
]);
export const PINNED_CRITERIA = Object.freeze(WIZARD_CRITERIA.slice(0, 5));
export const MORE_CRITERIA = Object.freeze(WIZARD_CRITERIA.slice(5));
/** Filtre etiketi (`legal-document-wizard.filters.<anahtar>`). */
export const FILTER_LABEL = Object.freeze({
  department: "department",
  process: "process",
  datasubjectCategory: "datasubject-category",
  datasubject: "datasubject",
  dataCategory: "data-category",
  data: "data",
  processingPurpose: "processing-purpose",
  processingLegalReason: "processing-legal-reason",
  receiverGroup: "receiver-group",
  thirdParty: "third-party",
  abroad: "abroad",
  country: "country",
  directCollectMethod: "direct-collect",
  otherCollectMethod: "other-collect",
  electronicStorage: "electronic-storage",
  physicalStorage: "physical-storage",
});
/** Birden çok seçimde ayrı metin üreten (aktör) kriterler — backend `actorList` ile aynı. */
export const ACTOR_CRITERIA = Object.freeze(["department", "process", "dataCategory", "datasubject", "thirdParty"]);
/** Boş seçim. */
export const emptyCriteria = () => Object.fromEntries(WIZARD_CRITERIA.map((k) => [k, []]));
/** Panelde (6–16) kaç seçim var. */
export const moreSelectedCount = (criteria) => MORE_CRITERIA.reduce((n, k) => n + (((criteria || {})[k] || []).length), 0);
/** Seçenek listesinden ada göre arama (istemci; seçenekler zaten envanterden daralmış gelir). */
export const searchOptions = (options = [], value = "", tr = (v) => v) => {
  const norm = (v) => String(v || "").replace(/\s+/g, " ").trim().toLocaleLowerCase("tr"); // veride çift boşluk olabiliyor
  const q = norm(value);
  return q ? (options || []).filter((o) => norm(tr(o.name)).includes(q)) : options || [];
};

/**
 * Tip listesi: organizasyonun şablon kategorileri (veriden gelir — Politika/Taahhütname dâhil, kriter 7) ve
 * yalnız hukuk yöneticisine görünen "Rapor".
 */
/**
 * TKP-174 md.37: organizasyonda aynı adlı kategori kaydı çoğalabiliyor (kütüphaneden her eklenişte) ve bazılarının şablonu yok.
 * Tip listesi şablonlardan türetilir: yalnız (ürün için) şablonu olan kategoriler, aynı adlılar TEK tipte (kimlik listesi).
 * `templates` verilmezse eski davranış (kategori başına bir tip).
 */
export const documentTypes = (categories, canSeeReports, templates) => {
  const list = categories || [];
  let types;
  if (!templates) {
    types = list.map((c) => ({ value: String(c._id), ids: [String(c._id)], name: c.name, kind: "CATEGORY" }));
  } else {
    const used = new Set(templates.map((t) => String((t.category && t.category._id) || t.category)).filter((id) => id && id !== "undefined"));
    const byName = new Map();
    for (const c of list) {
      if (!used.has(String(c._id))) continue;
      const key = typeLabelKey(c.name);
      const entry = byName.get(key) || { value: "", ids: [], name: c.name, kind: "CATEGORY" };
      entry.ids.push(String(c._id));
      byName.set(key, entry);
    }
    types = [...byName.values()].map((t) => ({ ...t, value: t.ids.join(",") }));
  }
  return canSeeReports ? [...types, { value: REPORT_TYPE, ids: [], name: null, kind: "REPORT" }] : types;
};

const typeLabelKey = (name) => String((name && (name.tr || name.en || Object.values(name).find(Boolean))) || "").trim().toLocaleLowerCase("tr");

/** Seçili tipin kategori kimlikleri (tek kimlik ya da virgülle birleşik liste). */
export const categoryIdsOf = (type) => (type && type !== REPORT_TYPE ? String(type).split(",").filter(Boolean) : []);

/** TKP-174 md.39: şablonun zorunlu kriterleri (organizasyon ve emekli kriterler hariç), sihirbazın sorduğu alanlar içinde. */
// TKP-197 (kullanıcı 2026-10-09): sihirbazda hiçbir filtre zorunlu değil — şablonun `requiredCriterias` alanı sihirbazı bağlamaz
export const requiredFields = () => [];
export const missingRequired = (template, criteria) => requiredFields(template).filter((k) => !((criteria || {})[k] || []).length);

/** TKP-174 md.38: bir kriterde birden çok seçim varsa "tek metin / her biri için ayrı" seçeneği anlamlı. */
export const hasMultiSelection = (criteria) => ACTOR_CRITERIA.some((k) => ((criteria || {})[k] || []).length > 1); // TKP-197: yalnız aktör kriterleri ayrı metin üretir

/** TKP-174 md.41: üst seçim alt seçeneği daraltır — birim seçiliyse süreç araması o birimlerle sınırlı. */
export const processSearchQuery = (value, criteria) => {
  const departments = ((criteria || {}).department || []).map((d) => d._id).filter(Boolean);
  return departments.length ? { name: { value }, department: { value: { $in: departments }, isObject: true } } : { name: { value } };
};

/** Seçili tip rapor mu (şablon adımı atlanır, kullanıcı Raporlar ekranına yönlendirilir). */
export const isReportType = (type) => type === REPORT_TYPE;

/** Sunucuya gidecek kriter: boş listeler gönderilmez (backend boş kriteri "filtre yok" sayar). */
export const toCriteriaPayload = (selection) =>
  Object.fromEntries(
    WIZARD_CRITERIA.filter((key) => ((selection || {})[key] || []).length > 0).map((key) => [key, selection[key].map((v) => ({ _id: v._id, name: v.name }))])
  );

/** Önizleme çağrılabilir mi: şablon seçilmiş olmalı (filtre zorunlu değil — şablonun kendi kriterleri kullanılır). */
export const canPreview = (state) => !!(state && state.template && state.template._id);

/** Tamamlanabilir mi: şablon seçili, zorunlu kriterler sağlanmış (md.39) ve önizleme envanterde veri bulmuş olmalı. */
export const canComplete = (state, preview) => canPreview(state) && missingRequired(state.template, state.criteria).length === 0 && !!preview && preview.hasData === true;

/** Adım sırası: tip seçilmeden şablon, şablon seçilmeden filtre adımı açılmaz. */
export const reachableStep = (state, step) => {
  if (step === "type") return true;
  if (step === "template") return !!(state && state.type) && !isReportType(state.type);
  if (step === "filters") return canPreview(state);
  return false;
};

/** Önizleme özeti: kaç doküman üretilecek, hangi aktörlerle. */
export const previewSummary = (preview) => {
  if (!preview) return { count: 0, actors: [], hasData: false };
  return {
    count: preview.combinations || 0,
    actors: Object.entries(preview.actors || {}).map(([field, actor]) => ({ field, name: actor.name })),
    hasData: !!preview.hasData,
  };
};
